import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import keycloak, { initKeycloak, redirectToKeycloakLogin } from "../../keycloak";

const paths = ["/auth/login"];

const LoggedOutLinks = () => {
  const [pathName, setPathName] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setPathName(location.pathname);
  }, [location]);

  const closeDrawer = () => setDrawerOpen(false);

  const handleGetStarted = async () => {
    closeDrawer();
    const redirectUri = `${window.location.origin}/register`;
    try {
      await initKeycloak();
    } catch {}
    keycloak.register({ redirectUri });
  };

  const handleBlogs = () => {
    closeDrawer();
    navigate("/blogs");
  };

  const handlePricing = () => {
    closeDrawer();
    navigate("/subscriptions");
  };

  const handleLogin = async () => {
    closeDrawer();
    await redirectToKeycloakLogin(`${window.location.origin}/lists`);
  };

  const handleAboutUs = () => {
    closeDrawer();
    navigate("/about-us");
  };

  const navItems = [
    {
      label: "About us",
      onClick: handleAboutUs,
      active: location.pathname === "/about-us",
    },
    {
      label: "Blogs",
      onClick: handleBlogs,
      active: location.pathname === "/blogs",
    },
    {
      label: "Pricing",
      onClick: handlePricing,
      active: location.pathname === "/subscriptions",
    },
  ];

  if (isMobile) {
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={handleLogin}
            variant="outlined"
            sx={{
              borderRadius: 999,
              py: 0.7,
              px: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 13,
              borderWidth: 1.5,
              borderColor: "#01aaf5",
              color: "#01aaf5",
              minWidth: 76,
              "&:hover": { borderWidth: 1.5, bgcolor: "rgba(1,170,245,0.06)" },
            }}
          >
            Login
          </Button>
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => setDrawerOpen(true)}
            sx={{
              width: 38,
              height: 38,
              bgcolor: "rgba(0,0,0,0.06)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={closeDrawer}
          transitionDuration={280}
          PaperProps={{
            sx: {
              width: "min(280px, 92vw)",
              overflow: "hidden",
              boxShadow: "-8px 0 32px rgba(15,23,42,0.12)",
            },
          }}
          ModalProps={{ keepMounted: true }}
          slotProps={{
            backdrop: {
              sx: {
                bgcolor: "rgba(15,23,42,0.42)",
                backdropFilter: "blur(2px)",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 2,
                borderBottom: "1px solid #eef0f2",
              }}
            >
              <Typography fontWeight={800} fontSize={17} letterSpacing={-0.2}>
                Menu
              </Typography>
              <IconButton
                onClick={closeDrawer}
                aria-label="Close menu"
                sx={{
                  position: "absolute",
                  right: 20,
                  width: 36,
                  height: 36,
                  bgcolor: "#f6f7f9",
                  "&:hover": { bgcolor: "#eef1f5" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                px: 2.5,
                pt: 2.5,
                pb: 2,
                gap: 1,
                flex: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={item.onClick}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: 3,
                      py: 1.5,
                      px: 2,
                      fontWeight: item.active ? 700 : 600,
                      color: item.active ? "#01aaf5" : "#0f172a",
                      bgcolor: item.active ? "#eef7ff" : "transparent",
                      textTransform: "none",
                      fontSize: 16,
                      letterSpacing: -0.15,
                      "&:hover": {
                        bgcolor: item.active ? "#e8f2ff" : "#f6f8fb",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              <Divider sx={{ my: 2, borderColor: "#eef0f2" }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  mt: 0.5,
                }}
              >
                {!paths.includes(pathName) && (
                  <Button
                    onClick={handleGetStarted}
                    fullWidth
                    variant="contained"
                    disableElevation
                    sx={{
                      borderRadius: 999,
                      py: 1.6,
                      fontWeight: 800,
                      textTransform: "none",
                      fontSize: 15,
                      bgcolor: "#01aaf5",
                      boxShadow: "0 8px 20px rgba(1,170,245,0.25)",
                      "&:hover": {
                        bgcolor: "#0096d6",
                        boxShadow: "0 10px 24px rgba(1,170,245,0.32)",
                      },
                    }}
                  >
                    Get Started
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <div className="text-btn-container">
      <Button
        onClick={handleAboutUs}
        size="small"
        className={`text-btn underline logged-out ${location.pathname === "/about-us" && "activeLink"}`}
      >
        About us
      </Button>
      <Button
        onClick={handleBlogs}
        size="small"
        className={`text-btn underline logged-out ${location.pathname === "/blogs" && "activeLink"}`}
      >
        Blogs
      </Button>
      <Button
        onClick={handlePricing}
        size="small"
        className={`text-btn underline logged-out ${location.pathname === "/subscriptions" && "activeLink"}`}
        sx={{ fontWeight: location.pathname === "/subscriptions" ? 700 : 600 }}
      >
        Pricing
      </Button>
      <Button
        onClick={handleLogin}
        size="small"
        className="text-btn underline logged-out login-btn"
      >
        Login
      </Button>
      {!paths.includes(pathName) && (
        <Button
          onClick={handleGetStarted}
          size="small"
          variant="contained"
          disableElevation
          color="primary"
          sx={{ color: "white", fontSize: "14px", letterSpacing: "1px" }}
        >
          Get Started
        </Button>
      )}
    </div>
  );
};

export default LoggedOutLinks;
